import axios, { type AxiosError, type AxiosInstance, type InternalAxiosRequestConfig } from 'axios'

// Transformation snake_case → camelCase récursive
function snakeToCamel(str: string): string {
  return str.replace(/_([a-z])/g, (_, letter: string) => letter.toUpperCase())
}

function transformKeys(data: unknown): unknown {
  if (Array.isArray(data)) {
    return data.map(transformKeys)
  }
  if (data !== null && typeof data === 'object') {
    return Object.fromEntries(
      Object.entries(data as Record<string, unknown>).map(([key, value]) => [
        snakeToCamel(key),
        transformKeys(value),
      ])
    )
  }
  return data
}

// Séparation du cycle de dépendance : le store auth est chargé à la demande
let _getAuthStore: (() => { accessToken: string | null; refreshToken: () => Promise<boolean> }) | null = null

export function setAuthStoreGetter(fn: () => { accessToken: string | null; refreshToken: () => Promise<boolean> }): void {
  _getAuthStore = fn
}

// Instance légère pour les appels auth (token/refresh) — pas d'intercepteur 401
// Base URL vide = URL relative → passe par Vite proxy (dev) ou nginx (prod) = same-origin
export const authClient: AxiosInstance = axios.create({
  timeout: 10_000,
  headers: { 'Content-Type': 'application/json' },
})

// Instance principale vers ship-api
const client: AxiosInstance = axios.create({
  timeout: 30_000,
  headers: { 'Content-Type': 'application/json' },
})

// Intercepteur request : injection du token Bearer
client.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  if (_getAuthStore) {
    const token = _getAuthStore().accessToken
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
  }
  return config
})

// Flag pour éviter la boucle infinie sur le refresh
let isRefreshing = false
let pendingRequests: Array<(token: string) => void> = []

function resolveQueue(token: string): void {
  pendingRequests.forEach(cb => cb(token))
  pendingRequests = []
}

// Intercepteur response : gestion 401/403/500 + transformation camelCase
client.interceptors.response.use(
  response => {
    if (response.data) {
      response.data = transformKeys(response.data)
    }
    return response
  },
  async (error: AxiosError) => {
    const status = error.response?.status
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean }

    if (status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        return new Promise(resolve => {
          pendingRequests.push((token: string) => {
            originalRequest.headers.Authorization = `Bearer ${token}`
            resolve(client(originalRequest))
          })
        })
      }

      originalRequest._retry = true
      isRefreshing = true

      try {
        if (_getAuthStore) {
          const success = await _getAuthStore().refreshToken()
          if (success && _getAuthStore) {
            const newToken = _getAuthStore().accessToken
            if (newToken) {
              resolveQueue(newToken)
              originalRequest.headers.Authorization = `Bearer ${newToken}`
              isRefreshing = false
              return client(originalRequest)
            }
          }
        }
        // Refresh échoué → redirect login via router (pas window.location pour éviter le rechargement complet)
        isRefreshing = false
        pendingRequests = []
        import('@/router').then(m => m.default.push({ name: 'AuthLogin' }))
        return Promise.reject(error)
      } catch {
        isRefreshing = false
        pendingRequests = []
        import('@/router').then(m => m.default.push({ name: 'AuthLogin' }))
        return Promise.reject(error)
      }
    }

    if (status === 403) {
      // Le store notifications est chargé via import dynamique pour éviter les dépendances circulaires
      import('./notifier').then(m => m.notifyError('Permissions insuffisantes pour cette action'))
    }

    if (status && status >= 500) {
      import('./notifier').then(m => m.notifyError('Erreur serveur — réessayez dans quelques instants'))
    }

    // Transformation des erreurs réponse aussi
    if (error.response?.data) {
      error.response.data = transformKeys(error.response.data)
    }

    return Promise.reject(error)
  }
)

export default client
